import { useState, useEffect, useCallback, useRef } from 'react'
import { indicators } from '@/indicators/registry'
import { getRemoteValue, saveRemote, hasUnsyncedLocalWrite } from './remotePreferences'
import { cellKey as buildCellKey } from '@/utils/normalizeSymbol'

// Clave reservada de overrides por símbolo/TF dentro de la config de cada
// indicador (diseño docs/indicators/diseno-snapshot-chart-bot.md §1.1):
//   config._bySymbolTf["BTCUSDT:1h"] = { ...override parcial... }
// El objeto plano de siempre sigue siendo el default/fallback universal —
// resolución {...global, ...(overrides[cellKey] || {})}, mismo patrón que
// mergeWithDefaults, un nivel más adentro. Sin migración: JSONB aditivo,
// el PATCH /chart-preferences existente reemplaza la clave completa (como
// hoy). El cellKey se construye con normalizeSymbol.js (port exacto del
// backend) — CCXT y plano caen en la misma celda.
// NOTA _version: los overrides viajan dentro de la config del indicador —
// sobreviven a los merges de versión con migración (spread {...defaults,
// ...saved}), pero un bump de _version SIN migración los descarta igual que
// cualquier otra pref del usuario (regla existente: nunca subir _version
// sin migración en migrateIndicatorConfig).
export const BY_SYMBOL_TF_KEY = '_bySymbolTf'

// Re-exportado para que los call sites construyan el cellKey con la misma
// normalización que el backend sin importar dos módulos.
export { buildCellKey }

const STORAGE_KEY = 'trading_bot_indicator_configs'
// Clave de la store de useIndicatorState (activeRegistry) — se consulta para
// los seedings de PRQ y SHOWICT (ver shouldSeedPrqEnabled /
// shouldSeedShowictEnabled).
const INDICATOR_STATE_KEY = 'chart_indicator_state_v2'

// Construir defaults dinámicamente desde los indicadores registrados
function buildDefaults() {
  const defaults = {}
  for (const ind of indicators) {
    defaults[ind.id] = ind.defaultConfig
  }
  return defaults
}

// Si cambió la versión de un indicador, resetea su config a los defaults
// actuales para evitar que parámetros obsoletos o de un algoritmo anterior
// sigan activos (por ejemplo Squeeze Breakout 1.0 → 1.1). Se aplica igual a
// lo leído de localStorage y a lo leído del backend (ver useIndicatorConfig).
// EXCEPCIÓN: los indicadores con migración explícita en
// migrateIndicatorConfig preservan las prefs del usuario en vez de descartar.
function mergeWithDefaults(parsed) {
  const defaults = buildDefaults()
  const merged = {}
  for (const id of Object.keys(defaults)) {
    const saved = parsed[id] || {}
    merged[id] = migrateIndicatorConfig(id, saved, defaults[id])
  }
  return merged
}

// Migración por indicador ANTES del descarte por _version.
// Caso showict_quantum (v1→v2 flag `invalidateOnOppositeSignal`, v2→v3 flag
// `fadeClosedSignals`): la config guardada ES de este mismo indicador, así
// que perder budgets, pliegues, badgePosition y toggles por un flag nuevo
// sería una regresión — se preserva TODO lo guardado (`{...defaults,
// ...saved}`) y solo se añaden los flags nuevos con su default. A diferencia
// del descarte-con-gracia de la config legacy showi_ict (indicador DISTINTO:
// sus prefs no aplican a la Quantum y esa sí se descarta sola al cargar,
// porque el merge solo conserva ids registrados).
// Regla: NUNCA subir _version de un indicador sin añadir aquí su migración.
function migrateIndicatorConfig(id, saved, defaults) {
  if (saved._version === defaults._version) {
    return { ...defaults, ...saved }
  }
  if (id === 'showict_quantum' && saved._version >= 1 && saved._version < defaults._version) {
    return { ...defaults, ...saved, _version: defaults._version }
  }
  // rango_quantum v1→v2 (Fase 0.6, 2026-08-15): añade `timeStopDays` (config
  // de serie, default 15). Mismo patrón que showict_quantum arriba — la
  // config guardada ES de este indicador, así que perder leverage/SL/TPs/
  // badgePosition/notificaciones por un parámetro nuevo sería una regresión;
  // se preserva TODO lo guardado y solo se añade la clave nueva con su
  // default.
  if (id === 'rango_quantum' && saved._version >= 1 && saved._version < defaults._version) {
    return { ...defaults, ...saved, _version: defaults._version }
  }
  return defaults
}

// ─── Semilla de enabled para patron_ruptura_quantum (PRQ) ────────────────────
// La migración v11 de useIndicatorState mapea activeRegistry.breakout_pattern
// === true → patron_ruptura_quantum: true, pero la config del indicador (esta
// store) queda con el default enabled:false y el usuario migrado vería el
// indicador activo en el menú sin pintar. Esta función decide si corresponde
// sembrar enabled:true:
//  - false si la config guardada de PRQ tiene clave propia `enabled`
//    (decisión explícita del usuario — incluida la que fija el menú al
//    activar, que escribe enabled:true — se respeta tal cual).
//  - true si el activeRegistry tiene breakout_pattern === true (legacy sin
//    migrar: la v11 corre en useIndicatorState y el orden de montaje de los
//    hooks no está garantizado, así que la state key puede venir aún en v10)
//    o patron_ruptura_quantum === true (ya migrado — p.ej. la state key
//    remota viaja con la v11 aplicada). Este segundo caso también daría true
//    para quien activó PRQ desde el menú, pero esos ya tienen enabled:true
//    explícito vía el handler del menú (regla anterior), así que en la
//    práctica solo cubre migrados.
export function shouldSeedPrqEnabled(rawConfigs, rawIndicatorState) {
  const prq = rawConfigs?.patron_ruptura_quantum
  if (prq && typeof prq === 'object' && 'enabled' in prq) return false
  const registry = rawIndicatorState?.activeRegistry
  return registry?.breakout_pattern === true || registry?.patron_ruptura_quantum === true
}

// Aplica la semilla sobre los configs YA mergeados con defaults, conservando
// cualquier otra clave guardada de PRQ. `rawConfigs` es el dict crudo (antes
// del merge) para detectar la decisión explícita del usuario.
function seedPrqEnabled(mergedConfigs, rawConfigs, rawIndicatorState) {
  if (!shouldSeedPrqEnabled(rawConfigs, rawIndicatorState)) return mergedConfigs
  return {
    ...mergedConfigs,
    patron_ruptura_quantum: { ...mergedConfigs.patron_ruptura_quantum, enabled: true },
  }
}

// ─── Semilla de enabled para showict_quantum ─────────────────────────────────
// Molde exacto del seeding de PRQ (ver arriba), sobre la migración v12 de
// useIndicatorState (activeRegistry.showi_ict === true → showict_quantum:
// true; el id legacy se retira en cualquier caso):
//  - false si la config guardada de SHOWICT tiene clave propia `enabled`
//    (decisión explícita del usuario, incluida la del menú — se respeta).
//  - true si el activeRegistry tiene showi_ict === true (legacy sin migrar:
//    la v12 corre en useIndicatorState y el orden de montaje de los hooks no
//    está garantizado, así que la state key puede venir aún en v11) o
//    showict_quantum === true (ya migrado — p.ej. la state key remota viaja
//    con la v12 aplicada). Este segundo caso también daría true para quien
//    activó SHOWICT desde el menú, pero esos ya tienen enabled:true explícito
//    vía el handler del menú (regla anterior), así que en la práctica solo
//    cubre migrados.
export function shouldSeedShowictEnabled(rawConfigs, rawIndicatorState) {
  const showict = rawConfigs?.showict_quantum
  if (showict && typeof showict === 'object' && 'enabled' in showict) return false
  const registry = rawIndicatorState?.activeRegistry
  return registry?.showi_ict === true || registry?.showict_quantum === true
}

// Aplica la semilla sobre los configs YA mergeados con defaults, conservando
// cualquier otra clave guardada de SHOWICT. `rawConfigs` es el dict crudo
// (antes del merge) para detectar la decisión explícita del usuario.
function seedShowictEnabled(mergedConfigs, rawConfigs, rawIndicatorState) {
  if (!shouldSeedShowictEnabled(rawConfigs, rawIndicatorState)) return mergedConfigs
  return {
    ...mergedConfigs,
    showict_quantum: { ...mergedConfigs.showict_quantum, enabled: true },
  }
}

// Lee la state key de useIndicatorState (puede estar sin migrar a v11/v12).
function readIndicatorState() {
  try {
    const raw = localStorage.getItem(INDICATOR_STATE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return seedShowictEnabled(seedPrqEnabled(buildDefaults(), null, readIndicatorState()), null, readIndicatorState())
    const parsed = JSON.parse(raw)
    return seedShowictEnabled(seedPrqEnabled(mergeWithDefaults(parsed), parsed, readIndicatorState()), parsed, readIndicatorState())
  } catch { }
  return buildDefaults()
}

function saveToStorage(configs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs))
  } catch { }
}

export function useIndicatorConfig() {
  const [configs, setConfigs] = useState(() => loadFromStorage())
  const remoteAppliedRef = useRef(false)
  const isFirstSaveRef = useRef(true)
  // Cuántos "primeros guardados" se han tolerado sin marcar cambio local real
  // (ver más abajo) — el montaje puede acumular varias escrituras legítimas
  // no-iniciadas-por-el-usuario (seeding de PRQ/SHOWICT, espejado de
  // useEmaSmaSync desde movingAverages) antes de que la hidratación remota
  // resuelva; un solo "primer guardado" tolerado no basta.
  const configsRef = useRef(configs)
  configsRef.current = configs

  useEffect(() => {
    saveToStorage(configs)
    if (isFirstSaveRef.current) {
      isFirstSaveRef.current = false
    } else {
      saveRemote(STORAGE_KEY, configs)
    }
  }, [configs])

  // Reconciliación con lo guardado en el backend para esta cuenta.
  useEffect(() => {
    let cancelled = false
    getRemoteValue(STORAGE_KEY).then((remote) => {
      if (cancelled || remoteAppliedRef.current) return
      // Bug de duplicado en el log de notificaciones ema_sma_quantum
      // (2026-08-12): un guardado local todavía sin confirmar en el
      // servidor (`hasUnsyncedLocalWrite`) es más nuevo que CUALQUIER
      // snapshot remoto que pueda llegar ahora — igual criterio que el P0 de
      // useIndicatorState (Tarea A). A diferencia de aquel hook, aquí NO se
      // usa un flag "cambió algo tras el montaje": este hook gobierna la
      // config de TODOS los indicadores en una única clave, con escrituras
      // legítimas de montaje ajenas al usuario (seeding PRQ/SHOWICT, espejado
      // useEmaSmaSync) que un flag tan amplio confundiría con una edición
      // real — probado que rompe la sincronización remota (E2E
      // ema_sma_notifications.spec.ts, "persiste tras F5" fallando en un
      // primer intento con ese flag). `hasUnsyncedLocalWrite` es más preciso:
      // sólo es cierto mientras haya un PATCH real en cola o sin confirmar
      // para esta clave.
      if (hasUnsyncedLocalWrite(STORAGE_KEY)) {
        remoteAppliedRef.current = true
        saveRemote(STORAGE_KEY, configsRef.current)
        return
      }
      if (!remote) return
      remoteAppliedRef.current = true
      isFirstSaveRef.current = true
      // Mismo seeding de PRQ/SHOWICT que en la carga local: la state key
      // remota puede viajar ya migrada (v11/v12) o aún con los legacy
      // breakout_pattern/showi_ict.
      getRemoteValue(INDICATOR_STATE_KEY).then((remoteState) => {
        if (cancelled) return
        setConfigs(seedShowictEnabled(seedPrqEnabled(mergeWithDefaults(remote), remote, remoteState), remote, remoteState))
      })
    })
    return () => { cancelled = true }
  }, [])
  // Persistencia de la semilla: el efecto de guardado de arriba la escribe en
  // localStorage en su primer pase; a remoto sólo llega con el siguiente
  // cambio real del usuario (isFirstSaveRef), igual que cualquier otra carga
  // inicial — no se fuerzan escrituras extra.

  const getConfig = useCallback((key, cell) => {
    const defaults = buildDefaults()
    const base = configs[key] ?? defaults[key]
    if (!cell) return base
    // Lectura por celda: {...global, ...(override de la celda || {})} —
    // la config global (sin _bySymbolTf) queda como fallback universal.
    const { [BY_SYMBOL_TF_KEY]: overrides, ...global } = base
    const cellOverride = overrides?.[cell]
    return cellOverride ? { ...global, ...cellOverride } : global
  }, [configs])

  const setConfig = useCallback((key, next, cell) => {
    setConfigs(prev => {
      const defaults = buildDefaults()
      const base = prev[key] ?? defaults[key]
      if (cell) {
        // Escritura por celda: el patch se aplica SOLO al override de la
        // celda — la config global queda intacta como fallback.
        const overrides = base[BY_SYMBOL_TF_KEY] ?? {}
        const { [BY_SYMBOL_TF_KEY]: _drop, ...global } = base
        const prevCell = overrides[cell] ?? {}
        const resolved = { ...global, ...prevCell }
        const patch = (typeof next === 'function' ? next(resolved) : next) ?? {}
        // `enabled` es activación del indicador en el chart (el menú la lee
        // en global), NO un parámetro de config por celda: siempre global.
        const { enabled, ...cellPatch } = patch
        // Override PARCIAL (diseño §1.1): solo las claves que cambian
        // respecto a la config resuelta — los paneles mandan {...cfg, key}
        // completo y sin este diff la celda se convertiría en una copia
        // stale de toda la config.
        const diff = {}
        for (const [k, v] of Object.entries(cellPatch)) {
          if (JSON.stringify(v) !== JSON.stringify(resolved[k])) diff[k] = v
        }
        const nextCell = { ...prevCell, ...diff }
        const nextOverrides = { ...overrides }
        if (Object.keys(nextCell).length) nextOverrides[cell] = nextCell
        else delete nextOverrides[cell]
        const updated = {
          ...global,
          ...(enabled === undefined ? {} : { enabled }),
          ...(Object.keys(nextOverrides).length ? { [BY_SYMBOL_TF_KEY]: nextOverrides } : {}),
        }
        if (JSON.stringify(updated) === JSON.stringify(prev[key])) {
          return prev
        }
        return { ...prev, [key]: updated }
      }
      const patch = typeof next === 'function' ? next(base) : next
      const updated = { ...base, ...patch }
      if (JSON.stringify(updated) === JSON.stringify(prev[key])) {
        return prev
      }
      return {
        ...prev,
        [key]: updated,
      }
    })
  }, [])

  const resetConfig = useCallback((key, cell) => {
    setConfigs(prev => {
      const defaults = buildDefaults()
      if (cell) {
        // Reset de celda: se borra el override — la celda vuelve al
        // fallback global. Si no quedan overrides, se retira la clave.
        const base = prev[key] ?? defaults[key]
        const overrides = { ...(base[BY_SYMBOL_TF_KEY] ?? {}) }
        delete overrides[cell]
        const { [BY_SYMBOL_TF_KEY]: _drop, ...global } = base
        const updated = Object.keys(overrides).length
          ? { ...global, [BY_SYMBOL_TF_KEY]: overrides }
          : global
        return { ...prev, [key]: updated }
      }
      return { ...prev, [key]: defaults[key] }
    })
  }, [])

  return { configs, getConfig, setConfig, resetConfig }
}
